/**
 * Vibesite Event DOM Capture
 *
 * A single document-level delegated listener that translates real user
 * interactions into bus emissions. Mounted exactly once per page from the
 * VibesitesProvider's effect.
 *
 * Resolution order per click / submit:
 *   1. closest('[data-vibesite-event]') — explicit annotation. Emits the
 *      named event with payload derived from the element (href, linkText,
 *      etc. for anchors). If matched, implicit rules are skipped entirely
 *      so an annotated tel: anchor doesn't fire both apply_now_clicked and
 *      phone_clicked.
 *   2. Implicit conventions on <a> (only if step 1 didn't match anywhere on
 *      the ancestor chain):
 *        href starts with "tel:"   → phone_clicked
 *        href starts with "mailto:" → email_clicked
 *
 * No off-host (outbound) detection — see catalog.ts for the rationale.
 */

import type { VibesiteEventBus } from './bus';
import {
  Event,
  isVibesiteEventName,
  type VibesiteEventName,
  type VibesiteEventEnvelope,
} from './catalog';

// ============================================================================
// Types
// ============================================================================

/**
 * Optional context the provider supplies so every emission carries the
 * standard envelope fields (page, propertyId, etc.) without each call site
 * having to construct them.
 */
export interface DomCaptureContextProvider {
  (): VibesiteEventEnvelope;
}

export interface DomCaptureOptions {
  /** Bus to emit into. */
  bus: VibesiteEventBus;
  /**
   * Returns the current envelope (page, path, propertyId, companyId...).
   * Called fresh on every emission so route changes pick up the new context.
   */
  getEnvelope?: DomCaptureContextProvider;
}

/**
 * Detach all listeners. Returned from `mountDomCapture()`.
 */
export type DomCaptureCleanup = () => void;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Read an anchor's visible text, falling back to aria-label / title for
 * icon-only links. Trimmed and length-capped so payloads stay small.
 */
function readLinkText(el: HTMLElement): string | undefined {
  const raw =
    (el.textContent || '').trim() ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    '';
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return undefined;
  return cleaned.length > 200 ? cleaned.slice(0, 200) : cleaned;
}

/**
 * Coerce an element's `target` attribute (or a sensible default) into the
 * payload string. Anchors without an explicit target get '_self'.
 */
function readTarget(el: HTMLElement): string | undefined {
  const t = el.getAttribute('target');
  return t || '_self';
}

/**
 * Walk up the ancestor chain of `target` (inclusive), stopping at the
 * document root, looking for the deepest element that carries
 * data-vibesite-event. Returns null if none found.
 *
 * We use Element.closest() exactly once and let it do the walk for us;
 * deepest match wins by definition because the event originates on the
 * inner element and bubbles.
 */
function findExplicitAnnotation(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const matched = target.closest('[data-vibesite-event]');
  return matched instanceof HTMLElement ? matched : null;
}

/**
 * Walk up looking for the nearest <a> ancestor with an actionable href.
 * Returns null when there isn't one.
 */
function findAnchorAncestor(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest('a[href]');
  return anchor instanceof HTMLAnchorElement ? anchor : null;
}

// ============================================================================
// Mount
// ============================================================================

/**
 * Attach the document-level delegated listeners. Idempotent at the bus level
 * — the provider guards against duplicate attachment via a useRef.
 */
export function mountDomCapture(options: DomCaptureOptions): DomCaptureCleanup {
  // SSR guard. The provider only invokes us inside a useEffect, but we
  // double-check so a misuse never throws on the server.
  if (typeof document === 'undefined') return () => {};

  const { bus, getEnvelope } = options;

  const buildEnvelope = (): VibesiteEventEnvelope => {
    const base = getEnvelope ? getEnvelope() : {};
    return { ...base, emittedAt: new Date().toISOString() };
  };

  const onClick = (event: MouseEvent) => {
    // Step 1: explicit annotation wins.
    const annotated = findExplicitAnnotation(event.target);
    if (annotated) {
      const name = annotated.getAttribute('data-vibesite-event') || '';
      if (!isVibesiteEventName(name)) {
        // Theme code referenced an event that isn't (or no longer is) in the
        // catalog. Warn loudly during dev — bus.emit() also no-ops on this.
        // eslint-disable-next-line no-console
        console.warn(
          `[vibesite-events] data-vibesite-event="${name}" is not in the catalog; ignored.`,
        );
        return;
      }

      const anchor =
        annotated instanceof HTMLAnchorElement ? annotated : findAnchorAncestor(annotated);

      const envelope = buildEnvelope();
      const payload: Record<string, unknown> = {
        ...envelope,
        linkText: readLinkText(annotated),
      };
      if (anchor) {
        payload.href = anchor.getAttribute('href') || undefined;
        payload.target = readTarget(anchor);
      }

      bus.emit(name as VibesiteEventName, payload as never);
      return;
    }

    // Step 2: implicit detection on bare anchors (tel: / mailto: only).
    const anchor = findAnchorAncestor(event.target);
    if (!anchor) return;

    const href = anchor.getAttribute('href') || '';
    const lowerHref = href.toLowerCase();
    const envelope = buildEnvelope();

    if (lowerHref.startsWith('tel:')) {
      bus.emit(Event.phone_clicked, {
        ...envelope,
        href,
        phoneNumber: href.slice(4) || undefined,
        linkText: readLinkText(anchor),
      });
      return;
    }

    if (lowerHref.startsWith('mailto:')) {
      // Strip query (mailto:foo@bar.com?subject=...) when extracting address.
      const rest = href.slice(7);
      const queryIdx = rest.indexOf('?');
      const address = queryIdx === -1 ? rest : rest.slice(0, queryIdx);
      bus.emit(Event.email_clicked, {
        ...envelope,
        href,
        emailAddress: address || undefined,
        linkText: readLinkText(anchor),
      });
      return;
    }

    // No off-host detection by design.
  };

  // Capture both primary clicks and middle-click / cmd-click via auxclick.
  // We listen at document level, in bubble phase, so React handlers run first
  // (they may stopPropagation; we don't care — analytics is best-effort, and
  // a stopped propagation usually means the app intentionally suppressed the
  // navigation, not the tracking).
  document.addEventListener('click', onClick);
  document.addEventListener('auxclick', onClick);

  return () => {
    document.removeEventListener('click', onClick);
    document.removeEventListener('auxclick', onClick);
  };
}
